import { FormEvent, useEffect, useRef, useState } from 'react'
import { templatesApi, type TemplateComment } from '../services/api'
import '../styles/blog.css'

// Reuses the shared od-blog-* comment styles (loaded via templates.css) so the
// template thread matches the blog thread exactly. Mirrors BlogComments.

const NAME_KEY = 'od-tpl-comment-name'
const BODY_MAX = 2000

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const seconds = Math.floor((Date.now() - then) / 1000)
  if (seconds < 60) return 'just now'
  const mins = Math.floor(seconds / 60)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function UpvoteIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 5v14M12 5l-6 6M12 5l6 6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
      />
    </svg>
  )
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?'
}

export default function TemplateComments({ slug }: { slug: string }) {
  const [comments, setComments] = useState<TemplateComment[]>([])
  const [allowComments, setAllowComments] = useState(false)
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState('')
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  const voting = useRef<Set<string>>(new Set())

  useEffect(() => {
    try {
      const saved = localStorage.getItem(NAME_KEY)
      if (saved) setName(saved)
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    let active = true
    setLoading(true)
    templatesApi
      .listComments(slug)
      .then((res) => {
        if (!active) return
        setComments(res.data || [])
        setAllowComments(Boolean(res.allow_comments))
      })
      .catch(() => {
        /* leave empty; non-fatal */
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [slug])

  async function toggleVote(comment: TemplateComment) {
    if (voting.current.has(comment.id)) return
    voting.current.add(comment.id)

    const nextVoted = !comment.has_voted
    setComments((prev) =>
      prev.map((c) =>
        c.id === comment.id
          ? { ...c, has_voted: nextVoted, upvotes: c.upvotes + (nextVoted ? 1 : -1) }
          : c,
      ),
    )

    try {
      const res = nextVoted
        ? await templatesApi.upvoteComment(comment.id)
        : await templatesApi.removeUpvoteComment(comment.id)
      setComments((prev) =>
        prev.map((c) =>
          c.id === comment.id ? { ...c, has_voted: res.has_voted, upvotes: res.upvotes } : c,
        ),
      )
    } catch {
      setComments((prev) =>
        prev.map((c) =>
          c.id === comment.id
            ? { ...c, has_voted: comment.has_voted, upvotes: comment.upvotes }
            : c,
        ),
      )
    } finally {
      voting.current.delete(comment.id)
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setFormError('')
    const trimmedName = name.trim()
    const trimmedBody = body.trim()
    if (!trimmedName || !trimmedBody) {
      setFormError('Please add your name and a comment.')
      return
    }

    setSubmitting(true)
    try {
      const res = await templatesApi.addComment(slug, {
        author_name: trimmedName,
        body: trimmedBody,
      })
      setComments((prev) => [res.data, ...prev])
      setBody('')
      try {
        localStorage.setItem(NAME_KEY, trimmedName)
      } catch {
        /* ignore */
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not post your comment.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="od-blog-comments" aria-label="Comments">
      <h2>
        Comments
        {comments.length > 0 && <span className="od-blog-comments-count">{comments.length}</span>}
      </h2>

      {allowComments ? (
        <form className="od-blog-comment-form" onSubmit={handleSubmit}>
          <input
            className="od-blog-comment-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            maxLength={80}
            aria-label="Your name"
          />
          <textarea
            className="od-blog-comment-body"
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
            placeholder="Share your experience with this template…"
            rows={4}
            aria-label="Your comment"
          />
          <div className="od-blog-comment-actions">
            <span className="od-blog-comment-counter">
              {body.length}/{BODY_MAX}
            </span>
            <button type="submit" disabled={submitting}>
              {submitting ? 'Posting…' : 'Post comment'}
            </button>
          </div>
          {formError && <p className="od-blog-comment-error">{formError}</p>}
        </form>
      ) : (
        <p className="od-blog-comments-closed">Comments are closed for this template.</p>
      )}

      {loading ? (
        <p className="od-blog-state">Loading comments…</p>
      ) : comments.length === 0 ? (
        <p className="od-blog-comments-empty">
          {allowComments ? 'Be the first to comment.' : 'No comments yet.'}
        </p>
      ) : (
        <ul className="od-blog-comment-list">
          {comments.map((comment) => (
            <li key={comment.id} className="od-blog-comment">
              <div className="od-blog-comment-avatar" aria-hidden="true">
                {initials(comment.author_name)}
              </div>
              <div className="od-blog-comment-main">
                <div className="od-blog-comment-head">
                  <span className="od-blog-comment-author">{comment.author_name}</span>
                  <span className="od-blog-comment-time">{relativeTime(comment.created_at)}</span>
                </div>
                <p className="od-blog-comment-text">{comment.body}</p>
              </div>
              <button
                type="button"
                className={`od-blog-upvote${comment.has_voted ? ' is-voted' : ''}`}
                onClick={() => toggleVote(comment)}
                aria-pressed={comment.has_voted}
                aria-label={comment.has_voted ? 'Remove upvote' : 'Upvote comment'}
              >
                <UpvoteIcon />
                <span>{comment.upvotes}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
